<template>
  <BottomWrapper
    id="otp-verification-wrapper"
    v-waiting.center="isLoading = { isLoading: loading }"
    :buttons="buttons"
    :err-info="errInfo"
    cancelable-request
    @select="handleVerifyOtp"
    @back="handleBack"
    @retry="cleanErr"
    @support-request:cancel="$emit('support-request:cancel', $event)"
  >
    <div class="otp-verification-container">
      <div class="center title-wrapper">
        <div>{{ $t('page.otp.verification.title') }}</div>
      </div>

      <div class="otp-description">
        <p>{{ $t('page.otp.verification.description', { email: guestData.email }) }}</p>
      </div>

      <div class="otp-input-container">
        <div class="otp-input-wrapper">
          <input
            v-model="otpCode"
            type="text"
            :placeholder="$t('page.otp.verification.placeholder')"
            :class="[{ warn: otpError }]"
            :maxlength="OTP_LENGTH"
            @input="handleOtpInput"
            @focus="otpError = false"
          />
        </div>
        <p v-if="otpError" class="warn-note-wrapper">
          <span class="star">*</span>
          {{ otpErrorMessage }}
        </p>
      </div>

      <div class="resend-container">
        <button v-if="canResend" type="button" class="resend-button" :disabled="isResending" @click="handleResendOtp">
          {{ isResending ? $t('page.otp.verification.resending') : $t('page.otp.verification.resend') }}
        </button>
        <div v-else class="resend-timer">
          {{ $t('page.otp.verification.resend_in', { seconds: remainingSeconds }) }}
        </div>
      </div>

      <div class="change-email-container">
        <button type="button" class="change-email-button" @click="handleChangeEmail">
          {{ $t('page.otp.verification.change_email') }}
        </button>
      </div>
    </div>
  </BottomWrapper>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import BottomWrapper from '@/views/Chatroom/BottomWrapper.vue';
import waiting from '@/components/loading/waiting';
import * as Locale from '@/plugins/i18n';
import { analytics } from '@/common/logs';
import { GENERAL_EVENTS, BTN_EVTS, CRITICAL_EVENTS } from '@/common/logs/events';

interface OtpVerificationData {
  email: string;
  name: string;
  phone: string;
  description: string;
}

@Component({
  components: {
    BottomWrapper,
  },
  directives: {
    waiting,
  },
})
export default class OtpVerification extends Vue {
  @Prop({ required: true }) readonly guestData!: OtpVerificationData;
  @Prop({ required: true }) readonly onVerifySuccess!: (otpCode: string) => Promise<void>;
  @Prop({ required: true }) readonly onResendOtp!: () => Promise<void>;

  readonly Locale = Locale;

  loading = false;
  isResending = false;
  otpCode = '';
  otpError = false;
  otpErrorMessage = '';
  resendAttempts = 0;
  maxResendAttempts = 3;
  remainingSeconds = 0;
  resendTimer: number | null = null;

  // OTP Configuration
  readonly OTP_LENGTH = 6;

  // Resend timeouts: 30s, 60s, 2m (120s)
  readonly resendTimeouts = [30, 60, 120];

  errInfo = {
    isErr: false,
    message: '',
  };

  get canResend() {
    return this.resendAttempts < this.maxResendAttempts && this.remainingSeconds === 0;
  }

  get buttons() {
    return [
      {
        name: this.$t('page.otp.verification.verify'),
        id: 'verify',
        isActive: this.otpCode.length === this.OTP_LENGTH && !this.loading,
      },
    ];
  }

  created() {
    document.title = 'OTP Verification';
    this.startResendTimer();
  }

  beforeDestroy() {
    this.clearResendTimer();
  }

  handleOtpInput(event: Event) {
    const target = event.target as HTMLInputElement;
    let value = target.value.replace(/\D/g, ''); // Only allow digits

    if (value.length > this.OTP_LENGTH) {
      value = value.substring(0, this.OTP_LENGTH);
    }

    this.otpCode = value;
    this.otpError = false;
  }

  async handleVerifyOtp() {
    if (this.otpCode.length !== this.OTP_LENGTH) {
      this.otpError = true;
      this.otpErrorMessage = this.$t('page.otp.verification.invalid_length') as string;
      return;
    }

    this.loading = true;
    this.otpError = false;

    try {
      await this.onVerifySuccess(this.otpCode);
    } catch (error: any) {
      this.handleVerificationError(error);
    } finally {
      this.loading = false;
    }
  }

  handleVerificationError(error: any) {
    const errorMessage = error?.message || error?.response?.data?.message || '';

    if (errorMessage.includes('Invalid')) {
      this.otpError = true;
      this.otpErrorMessage = this.$t('page.otp.verification.incorrect') as string;

      // Track incorrect OTP
      analytics.track({
        name: GENERAL_EVENTS.GUEST_OTP_INCORRECT,
        metadata: {
          email: this.guestData.email,
          error: errorMessage,
        },
      });
    } else {
      this.errInfo.isErr = true;
      this.errInfo.message = this.$t('page.otp.verification.general_error') as string;

      // Track general OTP error
      analytics.trackCriticalEvent({
        name: CRITICAL_EVENTS.GUEST_OTP_GENERAL_ERROR,
        metadata: {
          email: this.guestData.email,
          error: errorMessage,
        },
      });
    }
  }

  async handleResendOtp() {
    if (!this.canResend || this.isResending) return;

    this.isResending = true;
    this.resendAttempts++;

    // Track resend attempt
    analytics.track({
      name: GENERAL_EVENTS.GUEST_OTP_RESEND_ATTEMPT,
      metadata: {
        email: this.guestData.email,
        attempt: this.resendAttempts,
      },
    });

    try {
      await this.onResendOtp();
      this.startResendTimer();

      // Track successful resend
      analytics.track({
        name: GENERAL_EVENTS.GUEST_OTP_RESEND_SUCCESS,
        metadata: {
          email: this.guestData.email,
        },
      });
    } catch (error: any) {
      this.errInfo.isErr = true;
      this.errInfo.message = this.$t('page.otp.verification.resend_error') as string;

      // Track resend failure
      analytics.trackCriticalEvent({
        name: GENERAL_EVENTS.GUEST_OTP_RESEND_FAILED,
        metadata: {
          email: this.guestData.email,
          attempt: this.resendAttempts,
          error: error?.message || 'Unknown error',
        },
      });
    } finally {
      this.isResending = false;
    }
  }

  handleChangeEmail() {
    // Track change email click
    analytics.trackClick({
      name: BTN_EVTS.GUEST_OTP_CHANGE_EMAIL,
      metadata: {
        email: this.guestData.email,
      },
    });

    this.$emit('change-email');
  }

  handleBack() {
    this.$emit('back');
  }

  cleanErr() {
    this.errInfo.isErr = false;
    this.errInfo.message = '';
  }

  startResendTimer() {
    this.clearResendTimer();

    const timeoutIndex = Math.min(this.resendAttempts, this.resendTimeouts.length - 1);
    this.remainingSeconds = this.resendTimeouts[timeoutIndex];

    this.resendTimer = window.setInterval(() => {
      this.remainingSeconds--;
      if (this.remainingSeconds <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  clearResendTimer() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
    this.remainingSeconds = 0;
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/base.scss';
.otp-verification-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 0;
}

.title-wrapper {
  font-size: 1.8rem;
  font-weight: 600;
  color: $guest-otp-text-color;
  text-align: center;
}

.otp-description {
  text-align: center;
  color: $guest-otp-text-color;
  font-size: 1.4rem;
  line-height: 1.5;
}

.otp-input-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.otp-input-wrapper {
  width: 90%;
}

.otp-input-wrapper input {
  width: 100%;
  padding: 1.2rem;
  font-size: 1.8rem;
  text-align: center;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  letter-spacing: 0.2rem;
  font-weight: 600;

  &:focus {
    outline: none;
    border-color: $guest-otp-text-color;
  }

  &.warn {
    border-color: #dc2626;
  }
}

.resend-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 4rem;
}

.resend-button {
  background: none;
  border: none;
  color: $guest-otp-text-color;
  font-size: 1.4rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0.5rem;

  &:hover:not(:disabled) {
    color: $guest-otp-text-color;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.resend-timer {
  color: #6b7280;
  font-size: 1.4rem;
  text-align: center;
}

.change-email-container {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.change-email-button {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 1.3rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0.5rem;

  &:hover {
    color: #374151;
  }
}

.warn-note-wrapper {
  color: #dc2626;
  font-size: 1.2rem;
  text-align: center;
  margin-top: 0.5rem;

  .star {
    color: #dc2626;
    margin-right: 0.2rem;
  }
}
</style>
